import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { Component, EventEmitter, Output, ViewChild, effect, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { DataService } from "../service/data.service";
import { IBucketItem } from "../models/bucket-item.model";
import {
  AppSettingsService,
  OverlayPopupService,
  SpinnerService,
} from "../../../core";
import { PackageOverlayComponent } from "../package-overlay/package-overlay.component";
import { FormBuilder, FormControl, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { IBreadcrumbItem } from "../../../main/layout/tool-bar/model/breadcrumb.model";
import { BucketItemService } from "../service/bucket-item.service";
import { catchError, map, throwError } from "rxjs";
import { MessageService } from "../../../main";
import { IBucketToTransfer } from "../models/bucket-to-transfer.model";
import { BreadcrumbDataService } from "../../../main/layout/tool-bar/breadcrumb-service/breadcrumb-data.service";
import { IDestinationBucketListRequest } from "../models/destination-bucket-list-request.model";
import { DestinationPathDialogComponent } from "./destination-path-dialog/destination-path-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { IMoveItems } from "../models/move-items.model";
import { IDestinationPathResponse } from "../models/destination-path-response.model";
import { IMoveResponse } from "../models/move-response.model";
import { JustificationDialogComponent } from "../../../shared/justification-dialog/justification-dialog.component";
import { ErrorConstants } from "../constants/component-constants";
import { MatChipListbox, MatChipOption, MatChipRemove } from "@angular/material/chips";
import { TooltipDisableDirective } from "../../../shared/custom-directives/tooltip.directive";
import { MatIcon } from "@angular/material/icon";
import { MatError, MatLabel, MatFormField, MatInput } from "@angular/material/input";
import { MatButton } from "@angular/material/button";

@Component({
    selector: "app-move-items",
    templateUrl: "./move-items.component.html",
    styleUrls: ["./move-items.component.scss"],
    imports: [MatChipListbox, MatChipOption, TooltipDisableDirective, MatChipRemove, MatIcon, MatError, MatLabel, FormsModule, MatFormField, MatInput, ReactiveFormsModule, MatButton]
})
export class MoveItemsComponent {
  private dataService = inject(DataService);
  private readonly overlayPopupService = inject(OverlayPopupService);
  private fb = inject(FormBuilder);
  private bucketItemService = inject(BucketItemService);
  private messageService = inject(MessageService);
  private appSettingService = inject(AppSettingsService);
  private breadcrumbDataService = inject(BreadcrumbDataService);
  private spinnerService = inject(SpinnerService);
  private dialog = inject(MatDialog);

  @ViewChild("chipList") chipList: { errorState: boolean };
  @Output() cancelTransfer = new EventEmitter<boolean>();
  @Output() requestObject = new EventEmitter<object>();

  packagefolder: Partial<IBucketItem>[] = [];
  public requestData: IMoveItems;
  limitedpackagefolder: Partial<IBucketItem>[] = [];
  public destinationPath = new FormControl("", [Validators.required]);
  public parentData: Partial<IBreadcrumbItem>;
  public form = this.fb.group({
    destinationPath: this.destinationPath,
  });

  public currentBreadcrumbItem: IBreadcrumbItem = null;
  public transferListOptions: Partial<IBucketToTransfer>[] = [];
  public destionationBucketListRequest: IDestinationBucketListRequest;
  public selectedDestination: string;
  public selectedPath: string;
  public isEmptyDestination: boolean;
  private readonly featureToken = "bucket-items";
  private readonly surfaceToken = "move-items";
  private readonly _breadcrumb = toSignal(this.breadcrumbDataService.selectedBreadcrumb$);
  private readonly _allSelected = toSignal(this.dataService.getAllSelectedData());

  constructor() {
    effect(() => {
      const item = this._breadcrumb();
      if (item != null) this.currentBreadcrumbItem = item as IBreadcrumbItem;
    });
    effect(() => {
      const data = this._allSelected();
      if (data != null) {
        this.packagefolder = [...data];
        this.maintainlimitedpackagefolderlist();
        if (this.packagefolder?.length > 0 && this.chipList) {
          this.chipList.errorState = false;
        }
      }
    });
  }

  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  remove(packageitem: Partial<IBucketItem>): void {
    const index = this.packagefolder.indexOf(packageitem);
    if (index >= 0) {
      this.packagefolder.splice(index, 1);
    }
    this.dataService.setDataForCheckBox(this.packagefolder);
    this.maintainlimitedpackagefolderlist();
  }

  stringlength(packagefolder: Partial<IBucketItem>[]): boolean {
    if (packagefolder.length > 6) {
      return true;
    }
    return false;
  }

  overlaydisplay(packagefolder: [], e: any) {
    if (packagefolder.length <= 50) {
      this.overlayPopupService.open(PackageOverlayComponent, {
        data: packagefolder,
        element: e,
        displayPointer: true,
      });
    }
  }

  maintainlimitedpackagefolderlist() {
    if (this.packagefolder.length > 6) {
      this.limitedpackagefolder = this.packagefolder.slice(0, 6);
    } else {
      this.limitedpackagefolder = [...this.packagefolder];
    }
  }

  cancel() {
    this.cancelTransfer.emit(true);
  }

  openDestinationPopup() {
    let dialogRef = this.dialog.open(DestinationPathDialogComponent, {
      width: "829px",
      height: "550px",
      disableClose: true,
      data: this.currentBreadcrumbItem.DirectoryName,
    });
    dialogRef.afterClosed().subscribe((response: IDestinationPathResponse) => {
      if (response) {
        this.form.controls.destinationPath.setValue(response.path);
        this.selectedDestination = response.id;
        this.selectedPath = response.path;
      }
    });
  }

  moveItemOnSubmit() {
    if (this.packagefolder?.length === 0) {
      this.chipList.errorState = true;
    }
    if (this.selectedDestination === undefined) {
      this.isEmptyDestination = true;
    }
    this.destinationPath.markAsTouched();
    this.requestData = {
      ...this.requestData,
      BucketId: this.currentBreadcrumbItem.BucketId,
      BUCode: this.currentBreadcrumbItem.BUCode,
      BucketType: this.currentBreadcrumbItem.BucketType,
      DestinationBucketItemId: this.selectedDestination,
      SourceBucketItemIds: [...this.packagefolder.map((x) => x.Id)],
    };
    if (this.selectedPath) {
      const segments = this.selectedPath.split('/').filter(s => s && s.trim().length > 0);
      if (segments.length <= 2) {
        this.messageService.alert(
          ErrorConstants.FILES_CANNOT_MOVE_TO_BUCKET_LEVEL,
          this.appSettingService.toastrDisplayTime
        );
        return;
      }
    }
    if (
      this.requestData.SourceBucketItemIds.length > 0 &&
      this.requestData.DestinationBucketItemId !== undefined
    ) {
      this.spinnerService.show(true);
      this.bucketItemService
        .MoveItem(this.requestData)
        .pipe(
          map((res: Partial<IMoveResponse>) => {
            this.spinnerService.show(false);
            if (res.JustificationRequired) {
              this.dialog.open(JustificationDialogComponent, {
                width: "450px",
                disableClose: true
              }).afterClosed().subscribe(
                (response: { comment: string }) => {
                  if (response && response.comment) {
                    this.requestData.Comment = response.comment;
                    //It will make the process continue after giving the justification
                    this.moveItemOnSubmit();
                  }
                }
              );
            }
            else if (res.Response) {
              this.messageService.success(
                "Items successfully moved.",
                this.appSettingService.toastrDisplayTime
              );

              if (res.IsMalicious) {
                this.messageService.warning(
                  ErrorConstants.MALICIOUS_WARNING,
                  this.appSettingService.toastrDisplayTime
                );
              }

              this.requestObject.emit({
                selectedPath: this.selectedPath,
                destinationPackageId: res.DestinationPackageId,
              });
            }
            else if (!res.Response && res.IsMalicious) {
              this.messageService.warning(
                ErrorConstants.MALICIOUS_WARNING,
                this.appSettingService.toastrDisplayTime
              );
            }

          }),
          catchError((error: any) => {
            this.spinnerService.show(false);
            if (error.error.detail !== undefined) {
              this.messageService.alert(
                error.error.detail,
                this.appSettingService.toastrDisplayTime
              );
            } else {
              this.messageService.alert(
                "Move failed, please try again",
                this.appSettingService.toastrDisplayTime
              );
            }
            return throwError(() => {
              new Error(error.message);
            });
          })
        )
        .subscribe();
    }
  }

  public buildControlId(element: string, context?: string | number): string {
    const tokens = [
      this.featureToken,
      this.surfaceToken,
      this.slugify(element),
    ];
    if (context !== undefined && context !== null) {
      tokens.push(this.formatContextToken(context));
    }
    return tokens.join('-');
  }

  private formatContextToken(value: string | number): string {
    if (typeof value === 'number') {
      return value.toString().padStart(2, '0');
    }
    if (typeof value === 'string' && value.trim().length > 0) {
      return this.slugify(value);
    }
    return '00';
  }

  private slugify(value: string): string {
    return (value || 'na')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');
  }
}
